import { useMemo, useState, useEffect } from "react";
import {
  Modal,
  Drawer,
  Select,
  Checkbox,
  Empty,
  Table,
  Button,
  Input,
  Grid,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { Split } from "../../types/Programmazione";
import { PrecompiledSet } from "../../types/Entities";
import { computeHypLoadGrid } from "../Form/ExerciseProgressionEditor";
import { computeHypRepsGrid } from "../../utils/precompiledRuleApply";
import { useProgrammazioneMutations } from "../../hooks/useProgrammazioneMutations";
import DecimalInputNumber from "../../common/DecimalInputNumber";
import "./WeekCompareModal.css";

type Props = {
  open: boolean;
  onClose: () => void;
  split: Split;
  splitIndex: number;
  mutations?: ReturnType<typeof useProgrammazioneMutations>;
  /** Trainer — may edit Carico Suggerito (trialLoad) on top of work data. */
  canEdit?: boolean;
  /** Target athlete — may edit Work Load / Work Reps (work data). */
  isTargetAthlete?: boolean;
};

type FieldKey =
  | "workLoad"
  | "workReps"
  | "hypLoad"
  | "hypReps"
  | "trialLoad"
  | "repScheme";

type RowDatum = {
  key: string;
  /** Field label (sticky first col) or sub-row "Set N" label */
  rowLabel: string;
  isFieldHeader?: boolean;
  /** The compared field for this row (undefined for the "Set N" header row). */
  field?: FieldKey;
  /** Set index this row refers to (for the editable commit). */
  setIndex?: number;
  /** Cell values keyed by week index (string-stringified). Formatted display. */
  cells: Record<string, string>;
  /** Raw (un-formatted) values keyed by week index, for editable fields. */
  rawCells: Record<string, number | string | null>;
};

const FIELDS: { key: FieldKey; label: string }[] = [
  { key: "repScheme", label: "Rep Scheme" },
  { key: "trialLoad", label: "Carico Suggerito" },
  { key: "hypLoad", label: "Carico Ipotetico" },
  { key: "hypReps", label: "Reps Ipotetiche" },
  { key: "workLoad", label: "Work Load" },
  { key: "workReps", label: "Work Reps" },
];

const EM_DASH = "—";

function fmtLoad(v: number | string | null | undefined): string {
  if (v == null || v === "") return EM_DASH;
  return `${v} kg`;
}

function fmtReps(v: number | string | null | undefined): string {
  if (v == null || v === "") return EM_DASH;
  return String(v);
}

function fmtScheme(v: string | null | undefined): string {
  return v && v !== "" ? v : EM_DASH;
}

function readSetField(
  set: PrecompiledSet | undefined,
  field: FieldKey,
  hypLoad: number | null,
  hypReps: number | string | null,
  weekRepScheme: string | null | undefined,
): string {
  if (!set) return EM_DASH;
  switch (field) {
    case "workLoad":
      return fmtLoad(set.workLoad ?? null);
    case "workReps":
      return fmtReps(set.workReps ?? null);
    case "hypLoad":
      return fmtLoad(hypLoad);
    case "hypReps":
      return fmtReps(hypReps);
    case "trialLoad":
      return fmtLoad(set.trialLoad ?? null);
    case "repScheme":
      return fmtScheme(set.repScheme ?? weekRepScheme ?? null);
  }
}

function readSetRaw(
  set: PrecompiledSet | undefined,
  field: FieldKey,
): number | string | null {
  if (!set) return null;
  switch (field) {
    case "workLoad":
      return set.workLoad ?? null;
    case "workReps":
      return set.workReps ?? null;
    case "trialLoad":
      return set.trialLoad ?? null;
    case "repScheme":
      // Per-set override only (null = inherits the week scheme).
      return set.repScheme ?? null;
    default:
      return null;
  }
}

// Inline editable cell for the work-data / trial-load rows. Commits on blur /
// Enter (not on every keystroke) so each edit is a single programmazione save.
function EditableCell({
  raw,
  kind,
  placeholder,
  onCommit,
}: {
  raw: number | string | null;
  kind: "load" | "text";
  placeholder?: string;
  onCommit: (v: number | string | null) => void;
}) {
  const [local, setLocal] = useState<number | string | null>(raw);
  useEffect(() => setLocal(raw), [raw]);

  const commit = (next: number | string | null) => {
    if (next !== raw) onCommit(next);
  };

  if (kind === "text") {
    return (
      <Input
        size="small"
        value={local == null ? "" : String(local)}
        placeholder={placeholder}
        style={{ width: 96, textAlign: "right" }}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => commit(local === "" || local == null ? null : local)}
        onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
      />
    );
  }
  return (
    <DecimalInputNumber
      size="small"
      value={
        typeof local === "number"
          ? local
          : local == null || local === ""
            ? null
            : Number(local)
      }
      placeholder="kg"
      controls={false}
      style={{ width: 88 }}
      onChange={(v) => setLocal(v)}
      onBlur={() => commit(local == null ? null : Number(local))}
      onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
    />
  );
}

export default function WeekCompareModal({
  open,
  onClose,
  split,
  splitIndex,
  mutations,
  canEdit = false,
  isTargetAthlete = false,
}: Props) {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  /* Exercises with >=1 week (eligible for selection); 0-week ones shown disabled. */
  const allExercises = useMemo(
    () => split.exercises ?? [],
    [split.exercises],
  );
  const eligibleIdxs = useMemo(
    () =>
      allExercises
        .map((e, i) => ({ e, i }))
        .filter(({ e }) => (e.progression?.weeks?.length ?? 0) > 0)
        .map(({ i }) => i),
    [allExercises],
  );

  const [exerciseIdx, setExerciseIdx] = useState<number | null>(null);

  /* Auto-select first eligible exercise on open or when split changes. */
  useEffect(() => {
    if (!open) return;
    if (exerciseIdx == null || !eligibleIdxs.includes(exerciseIdx)) {
      setExerciseIdx(eligibleIdxs[0] ?? null);
    }
  }, [open, eligibleIdxs, exerciseIdx]);

  const selectedExercise =
    exerciseIdx != null ? allExercises[exerciseIdx] : undefined;
  const weeks = useMemo(
    () => selectedExercise?.progression?.weeks ?? [],
    [selectedExercise],
  );

  /* Week selector state. Reset only when switching exercise or when the number
     of weeks changes — NOT on a cell edit (which produces a new `weeks`
     reference), so the user's selection survives editing. */
  const [selectedWeeks, setSelectedWeeks] = useState<number[]>([]);
  useEffect(() => {
    setSelectedWeeks(weeks.map((_, i) => i));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exerciseIdx, weeks.length]);

  /* Hyp grids — recomputed when weeks change. */
  const hypLoadGrid = useMemo(() => computeHypLoadGrid(weeks), [weeks]);
  const hypRepsGrid = useMemo(() => computeHypRepsGrid(weeks), [weeks]);

  const allSelected =
    weeks.length > 0 && selectedWeeks.length === weeks.length;
  const noneSelected = selectedWeeks.length === 0;
  const indeterminate = !allSelected && !noneSelected;

  const toggleAll = (checked: boolean) => {
    setSelectedWeeks(checked ? weeks.map((_, i) => i) : []);
  };

  const sortedSelected = useMemo(
    () => [...selectedWeeks].sort((a, b) => a - b),
    [selectedWeeks],
  );

  const maxSetCount = useMemo(() => {
    let m = 0;
    sortedSelected.forEach((wi) => {
      const n = weeks[wi]?.sets?.length ?? 0;
      if (n > m) m = n;
    });
    return m;
  }, [sortedSelected, weeks]);

  // Editability per field (work data → trainer + target athlete; trial load →
  // trainer only). Requires the mutations bundle + a resolved exercise.
  const canEditField = (field: FieldKey): boolean => {
    if (!mutations || exerciseIdx == null) return false;
    if (field === "workLoad" || field === "workReps")
      return canEdit || isTargetAthlete;
    // Trainer-only fields.
    if (field === "trialLoad" || field === "repScheme") return canEdit;
    return false;
  };

  const commitField = (
    field: FieldKey,
    weekIndex: number,
    setIndex: number,
    value: number | string | null,
  ) => {
    if (!mutations || exerciseIdx == null) return;
    if (field === "workReps") {
      mutations.updateProgressionSet(splitIndex, exerciseIdx, weekIndex, setIndex, {
        workReps: value === "" || value == null ? null : value,
      });
    } else if (field === "workLoad") {
      mutations.updateProgressionSet(splitIndex, exerciseIdx, weekIndex, setIndex, {
        workLoad: value == null || value === "" ? null : Number(value),
      });
    } else if (field === "trialLoad") {
      mutations.updateProgressionSet(splitIndex, exerciseIdx, weekIndex, setIndex, {
        trialLoad: value == null || value === "" ? null : Number(value),
      });
    } else if (field === "repScheme") {
      mutations.updateProgressionSet(splitIndex, exerciseIdx, weekIndex, setIndex, {
        repScheme: value == null || value === "" ? null : String(value),
      });
    }
  };

  /* Build rows grouped by Set: a "Set N" section header followed by the six
     field rows (in FIELDS order) compared across the selected weeks. For a
     single-set exercise the section header is dropped so the table reads as a
     plain field × week matrix — more compact and immediately scannable. */
  const rows: RowDatum[] = useMemo(() => {
    if (noneSelected) return [];
    const out: RowDatum[] = [];
    const showSetHeaders = maxSetCount > 1;
    for (let si = 0; si < Math.max(1, maxSetCount); si++) {
      if (showSetHeaders) {
        const headerCells: Record<string, string> = {};
        sortedSelected.forEach((wi) => {
          headerCells[String(wi)] = "";
        });
        out.push({
          key: `set-${si}-h`,
          rowLabel: `Set ${si + 1}`,
          isFieldHeader: true,
          cells: headerCells,
          rawCells: {},
        });
      }
      FIELDS.forEach(({ key, label }) => {
        const cells: Record<string, string> = {};
        const rawCells: Record<string, number | string | null> = {};
        sortedSelected.forEach((wi) => {
          const w = weeks[wi];
          const s = w?.sets?.[si];
          cells[String(wi)] = readSetField(
            s,
            key,
            hypLoadGrid[wi]?.[si] ?? null,
            hypRepsGrid[wi]?.[si] ?? null,
            w?.repScheme,
          );
          rawCells[String(wi)] = readSetRaw(s, key);
        });
        out.push({
          key: `set-${si}-${key}`,
          rowLabel: label,
          field: key,
          setIndex: si,
          cells,
          rawCells,
        });
      });
    }
    return out;
  }, [
    noneSelected,
    sortedSelected,
    weeks,
    hypLoadGrid,
    hypRepsGrid,
    maxSetCount,
  ]);

  const columns: ColumnsType<RowDatum> = useMemo(() => {
    const cols: ColumnsType<RowDatum> = [
      {
        title: "",
        dataIndex: "rowLabel",
        key: "rowLabel",
        fixed: "left",
        width: 150,
        render: (text: string, row) => (
          <span className={row.isFieldHeader ? "wcm-set-header" : "wcm-field-label"}>
            {text}
          </span>
        ),
      },
    ];
    sortedSelected.forEach((wi) => {
      cols.push({
        title: `Sett. ${wi + 1}`,
        dataIndex: ["cells", String(wi)],
        key: `w-${wi}`,
        align: "right",
        width: 120,
        render: (_: unknown, row) => {
          if (row.isFieldHeader || row.field == null || row.setIndex == null) {
            const v = row.cells[String(wi)] ?? "";
            if (v === "" || v === EM_DASH) {
              return <span className="wcm-empty-cell">{EM_DASH}</span>;
            }
            return v;
          }
          // Set may not exist on this particular week (ragged weeks).
          const setExists = (weeks[wi]?.sets?.length ?? 0) > row.setIndex;
          if (canEditField(row.field) && setExists) {
            const isText =
              row.field === "workReps" || row.field === "repScheme";
            const placeholder =
              row.field === "workReps"
                ? "reps"
                : row.field === "repScheme"
                  ? weeks[wi]?.repScheme || "es. 8-12"
                  : undefined;
            return (
              <EditableCell
                raw={row.rawCells[String(wi)] ?? null}
                kind={isText ? "text" : "load"}
                placeholder={placeholder}
                onCommit={(value) =>
                  commitField(row.field!, wi, row.setIndex!, value)
                }
              />
            );
          }
          const v = row.cells[String(wi)] ?? "";
          if (v === "" || v === EM_DASH) {
            return <span className="wcm-empty-cell">{EM_DASH}</span>;
          }
          return v;
        },
      });
    });
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedSelected, weeks, mutations, exerciseIdx, canEdit, isTargetAthlete, splitIndex]);

  /* Empty-state branches per UX §6. */
  const isOneWeek = weeks.length === 1;

  const editableHint =
    mutations && (canEdit || isTargetAthlete) ? (
      <div className="wcm-edit-hint">
        Work Load e Work Reps
        {canEdit ? ", Carico Suggerito e Rep Scheme" : ""} sono modificabili
        direttamente nelle celle.
      </div>
    ) : null;

  const body = (
    <div>
      {/* Esercizio picker */}
      <div className="wcm-header-row">
        <span className="wcm-header-row-label">Esercizio:</span>
        <Select
          showSearch
          value={exerciseIdx ?? undefined}
          onChange={(v) => setExerciseIdx(v)}
          placeholder="Seleziona un esercizio"
          style={{ flex: 1, maxWidth: isMobile ? "100%" : 420 }}
          optionFilterProp="label"
          options={allExercises.map((e, i) => {
            const name =
              e.customName ||
              (e.type === "catalog"
                ? "Esercizio da catalogo"
                : "Esercizio personalizzato");
            const nWeeks = e.progression?.weeks?.length ?? 0;
            const disabled = nWeeks === 0;
            return {
              value: i,
              disabled,
              label: `${name}${
                disabled ? " · Nessuna settimana" : ` · ${nWeeks} settimane`
              }`,
            };
          })}
        />
      </div>

      {/* Weeks picker — hidden when only one week exists */}
      {weeks.length > 1 && (
        <div className="wcm-header-row">
          <span className="wcm-header-row-label">Settimane:</span>
          <div className="wcm-week-chips">
            <Checkbox
              indeterminate={indeterminate}
              checked={allSelected}
              onChange={(e) => toggleAll(e.target.checked)}
            >
              Tutte
            </Checkbox>
            <Checkbox.Group
              value={selectedWeeks}
              onChange={(vals) => setSelectedWeeks(vals as number[])}
              options={weeks.map((_, i) => ({
                label: `S${i + 1}`,
                value: i,
              }))}
            />
          </div>
          <Button
            type="link"
            className="wcm-reset-link"
            onClick={() => toggleAll(true)}
            disabled={allSelected}
          >
            Reset
          </Button>
        </div>
      )}

      {editableHint}

      {/* Body */}
      <div className="wcm-table-wrap">
        {!selectedExercise ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Scegli un esercizio dall'elenco in alto per iniziare il confronto."
          />
        ) : isOneWeek ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Il confronto si attiva da due settimane in poi per questo esercizio. Scegli un altro esercizio o aggiungi una settimana."
          />
        ) : noneSelected ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <>
                Seleziona almeno una settimana per vedere il confronto.{" "}
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0 }}
                  onClick={() => toggleAll(true)}
                >
                  Seleziona tutte
                </Button>
              </>
            }
          />
        ) : (
          <Table<RowDatum>
            columns={columns}
            dataSource={rows}
            pagination={false}
            size="small"
            scroll={{ x: "max-content" }}
            rowClassName={(row) =>
              row.isFieldHeader ? "wcm-set-header-row" : "wcm-set-row"
            }
          />
        )}
      </div>
    </div>
  );

  const title = `Confronta settimane — ${split.dayName || "Split"}`;

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        placement="bottom"
        height="100vh"
        title={title}
        destroyOnClose
      >
        {body}
      </Drawer>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      footer={
        <Button onClick={onClose} type="primary">
          Chiudi
        </Button>
      }
      width={Math.min(1080, Math.round(window.innerWidth * 0.9))}
      style={{ top: 24 }}
      styles={{ body: { maxHeight: "calc(80vh - 160px)", overflowY: "auto" } }}
      destroyOnClose
      centered={false}
    >
      {body}
    </Modal>
  );
}
